import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { LanguageToolPage } from '@/components/astro/new-design-25/SEO/language-tool-page';
import { hreflangLinks, seo } from '@/seo/seo';

const faqTitle = 'Hindi text to speech FAQ';
const faqDescription =
  'Quick answers about converting Hindi text to speech with Listening.';
const faqs = [
  {
    question: 'Which Hindi voices are available?',
    answer:
      'Listening offers natural Indian Hindi voices in male and female options, trained on real native speech rather than generic South Asian English voices forced to read Devanagari.',
  },
  {
    question: 'Does it read Devanagari script directly?',
    answer:
      'Yes. The converter reads Devanagari with correct conjuncts, matras, and proper handling of common Sanskrit-derived academic and technical terminology.',
  },
  {
    question: 'Can I use this for UPSC preparation or college coursework?',
    answer:
      'Yes. Convert NCERTs, current affairs articles, course chapters, or notes into Hindi audio for revision during commutes and breaks. Adjustable speed helps for tough passages.',
  },
  {
    question: 'How many characters can I convert at once?',
    answer:
      'The free converter on this page is capped at 10,000 characters. The Listening app removes the cap and handles full documents and PDFs with the same Hindi voices.',
  },
];

const heroTitle = 'Hindi Text to Speech';
const heroDescription =
  'Hindi text to speech with natural Indian voices. Full Devanagari support, accurate pronunciation of academic and technical Hindi vocabulary, and the right rhythm for serious long-form listening.';

const featureTitle = 'the Hindi TTS that actually sounds Indian:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'Hindi voices';
const featureVariant = 'default';
const featureCtaText = 'Try now';

const featureFeatures = [
  {
    title: 'Native Indian Voices',
    description:
      'Natural Hindi voices trained in India, not generic regional accents pretending to be Hindi.',
  },
  {
    title: 'Full Devanagari Support',
    description:
      'Reads Devanagari script directly with correct conjuncts, matras, and shirorekha-aware pronunciation.',
  },
  {
    title: 'Academic and Technical Vocabulary',
    description:
      'Handles Hindi academic prose, Sanskrit-derived terminology, and modern technical words without breaking.',
  },
  {
    title: 'Built for Long-Form Listening',
    description:
      'Designed for chapters, articles, and study material, with adjustable speed for review and revision.',
  },
];

const solutionBenefitsData = {
  title: 'Why Hindi listeners pick this TTS over the defaults',
  items: [
    {
      text: 'Natural Indian Hindi voices, not a Hindi label slapped on a generic South Asian accent',
      highlight: 'Natural Indian Hindi voices',
    },
    {
      text: 'Accurate Devanagari pronunciation, including conjunct consonants and vowel matras',
      highlight: 'Devanagari pronunciation',
    },
    {
      text: 'Proper handling of Hindi academic and technical terminology used in universities and entrance exams',
      highlight: 'academic and technical terminology',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x, useful for revision passes before exams',
      highlight: '0.5x to 4x',
    },
  ],
};

const useCasesData = {
  title: 'How students and professionals use Hindi text to speech',
  description:
    'From UPSC aspirants to Hindi-medium engineering students, here is how our Hindi voices show up in daily study.',
  useCases: [
    {
      title: 'UPSC and State Civil Services Aspirant',
      challenge:
        'Get through huge amounts of Hindi-medium current affairs, NCERT material, and standard reference texts.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Convert NCERTs, magazines, and notes to Hindi audio for revision during travel, walks, or breaks.',
        },
        {
          label: 'Generic TTS',
          content:
            'Robotic Hindi mispronounces key vocabulary, which is the last thing you need before a competitive exam.',
        },
      ],
    },
    {
      title: 'Hindi-Medium College or University Student',
      challenge:
        'Keep up with course readings and reference books, many of which are dense Hindi academic prose.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Listen to chapters and notes in natural Hindi, slow it down on tough passages, share audio with classmates.',
        },
        {
          label: 'Generic TTS',
          content:
            'Misreads Sanskrit-derived terminology and breaks on conjuncts, making serious academic Hindi unusable.',
        },
      ],
    },
    {
      title: 'Hindi Educator Creating Audio Lessons',
      challenge:
        'Produce Hindi audio material for school students, e-learning platforms, or YouTube channels.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Generate clean Hindi read-alongs from your written scripts, with the same voice across episodes for consistency.',
        },
        {
          label: 'Generic TTS',
          content:
            'Mechanical phrasing and inconsistent pronunciation make audio lessons feel cheap and amateurish.',
        },
      ],
    },
    {
      title: 'Diaspora Speaker Reconnecting with Hindi',
      challenge:
        'Build Hindi reading and listening confidence as an adult who grew up speaking Hindi but does not read fluently.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Paste any Hindi article, slow the playback, and follow along with synced highlighting in the app.',
        },
        {
          label: 'Generic TTS',
          content:
            'Wooden delivery and mispronunciation actively makes the language sound foreign instead of familiar.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'What Hindi Text to Speech Has to Get Right',
    content:
      'Hindi TTS is hard because Devanagari is dense with information. A single character can include a consonant base, a vowel matra, a nukta, and a halant — and the way conjuncts combine changes how the whole syllable should be pronounced. Most "Hindi" voices on free TTS tools are not really Hindi at all. They are South Asian English voices forced to read Devanagari, which is why they mispronounce common academic words and stumble on conjuncts.',
    contentSecondary:
      'Listening uses Hindi voices trained on native Indian Hindi audio, including the kind of formal Hindi used in textbooks, current affairs magazines, and competitive exam material. Conjuncts pronounce as conjuncts, not as broken syllables. Sanskrit-derived terminology comes out the way a teacher would say it. The result is Hindi audio you can listen to for a 45-minute revision session without your ear fighting the voice.',
  },
  {
    title: 'Hindi Text to Speech for Study, Exams, and Daily Reading',
    content:
      'For exam aspirants and Hindi-medium students, listening is a multiplier. Reading is slow when the material is dense, but listening during the dead time of a day — commutes, walks, household tasks — turns hours of otherwise lost time into revision. Paste any Hindi text into the converter on this page, generate audio, and play it like a podcast.',
    contentSecondary:
      'The Listening app extends the same Hindi voices across PDFs, articles, and the Chrome extension, so the same content you study on your phone in the morning can be sent from your laptop in the evening. The free converter opens a 24-hour read-along link with synced text, audio, speed controls, and a playbar. For long-form Hindi readers, that is the difference between TTS as a novelty and TTS as part of how you actually study.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/hindi-text-to-speech')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Hindi Text to Speech — Natural Indian Hindi AI Voices | Listening',
        description:
          'Hindi text to speech with natural Indian voices. Full Devanagari support, accurate academic and technical pronunciation. Free demo, no sign-up.',
      }),
    ],
    links: [...hreflangLinks('/hindi-text-to-speech')],
  }),
  component: () => (
    <LanguageToolPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription={faqDescription}
      faqTitle={faqTitle}
      faqs={faqs}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      preferredLang='hi'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
